import React, { useState, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import Panel from './layout/Panel';
import SearchField from './layout/SearchField';
import Spacer from './layout/Spacer';
import ListView from './listComponent/ListView';
import ListItem from './listComponent/ListItem';
import Button from './layout/Button';
import PlaceholderContent from './layout/PlaceholderContent';
import { Spinner } from './Iconset';
import { LayoutOrientations } from '../globals'
import TextUtils from '../classes/utilities/TextUtils';
import { useTypedSelector } from '../state/hooks';
import GQLscenario from '../classes/api/GQL/scenario/GQLscenario';

export default function ScenarioPicker(props) 
{
	const selectedCompany = useTypedSelector(state => state.userModule.currentOrganization, shallowEqual);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedScenario, setSelectedScenario] = useState<any>(null);
	const [scenarioList, setScenarioList] = useState<Array<{id: string, name: string, databaseId: string}>>();
	const [error, setError] = useState<string>("");

	useEffect(() => 
	{
		// to fix react error, as per https://stackoverflow.com/a/53572588/618726
		async function fetchList() 
		{
			GQLscenario.listScenarios()
				.then((scenarios) =>
				{
					//console.log(data);
					setScenarioList(scenarios);
				})
				.catch((error) => 
				{
					console.error(error);
					setError("Fetching scenario list failed: " + error.message);
				});
		}

		if (selectedCompany) 
		{
			fetchList();
		}
	}, [selectedCompany]);

	function renderScenarioList() 
	{
		if (selectedCompany === undefined) 
		{
			return (
				<PlaceholderContent>
					Select Organization to View Scenarios.
				</PlaceholderContent>
			);
		}

		if (error) 
		{
			return (
				<PlaceholderContent>
					<div>
						{error}
					</div>
				</PlaceholderContent>
			);
		}

		if (!scenarioList) 
		{
			return (
				<PlaceholderContent>
					<Spinner size={60} />
					<Spacer size={{ y: "12px" }} />
					<p>Fetching scenarios...</p>
				</PlaceholderContent>
			);
		}

		const filteredList = getFilteredScenarios();

		if (!filteredList.length) 
		{
			return (
				<PlaceholderContent>
					<p>No scenarios found in organization!</p>
				</PlaceholderContent>
			);
		}

		return (
			<Panel>
				<ListView
					data-name={"scenario-list"}
					selectedItems={new Set().add(selectedScenario ? selectedScenario.nodeId : undefined)}
					selectionChangedCallback={(arScenarioset) => 
					{
						setSelectedScenario(arScenarioset.values().next().value);
					}}
					doubleClickCallback={onConfirm}
					dndDefaults={{
						isDraggable: false,
						isDroppable: false,
					}}
				>
					{
						getFilteredScenarios()
					}
				</ListView>
			</Panel>
		);
	}

	function getFilteredScenarios() 
	{
		const filtered = new Array<JSX.Element>();

		scenarioList?.forEach(scenario => 
		{
			if (TextUtils.searchString(scenario.name || '', searchQuery)) 
			{
				filtered.push(
					<ListItem
						key={scenario.id}
						nodeId={scenario.id}
						label={scenario.name}
						id={String(scenario.databaseId)}
					/>
				);
			}
		});

		return filtered;
	}


	function onConfirm() 
	{
		if (selectedScenario && props.onSelectedCallback) 
		{
			props.onSelectedCallback(selectedScenario);
		}

		if (props.onShutdown) 
		{
			props.onShutdown();
		}
	}

	return (
		<Panel style={{
			maxHeight: "600px",
			minWidth: "600px",
			maxWidth: "600px",
		}}
		data-name={"scenario-grid-root"}
		>
			<div className="largeText">
				Scenarios
			</div>
			<Spacer size={{ y: "2px" }} />
			<Panel className="solid composite"
				style={{
					minHeight: "200px",
					minWidth: "200px",
				}}
			>
				<SearchField value={searchQuery} onChangeCallback={(v) => setSearchQuery(v)} />
				{
					renderScenarioList()
				}
			</Panel>
			<Spacer size={{ y: "4px" }} />
			<Panel orientation={LayoutOrientations.Horizontal}
				size={{ y: "24px" }}
			>
				{
					!selectedCompany ? null :
						<Button label="Confirm"
							silent={true}
							data-name={"confirm-scenario-select-button"}
							onClick={onConfirm}
						/>
				}
			</Panel>
		</Panel>
	);
}